import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import WaterfallCard from "../components/WaterfallCard";
import SafetyBanner from "../components/SafetyBanner";
import { Search, AlertTriangle, RefreshCw } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_waterfall-finder-1/artifacts/qu3csjl4_lumina-edit-1782098389929.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState({ countries: [], states: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useSEO({
    title: "WaterfallFinder — Find your next waterfall adventure",
    description:
      "Discover the world's best waterfalls. Photos, maps, height, trail stats and safety tips for 100+ stunning waterfalls.",
    image: HERO_IMG,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "WaterfallFinder",
      url: typeof window !== "undefined" ? window.location.origin : "",
    },
  });

  const loadHome = useCallback(async () => {
    setLoading(true);
    setLoadErr(false);
    try {
      // Fire a cheap warm-up ping in parallel so the pod's Mongo pool is primed.
      // Failures are silent — the real requests below still drive UI state.
      api.get("/warmup").catch(() => {});
      const [f, c] = await Promise.all([
        api.get("/waterfalls", { params: { featured: true, limit: 12 } }),
        api.get("/categories"),
      ]);
      setFeatured(f.data.items || []);
      setCategories(c.data || { countries: [], states: [], cities: [] });
    } catch {
      setLoadErr(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHome(); }, [loadHome]);

  const onSearch = (e) => {
    e.preventDefault();
    nav(`/waterfalls?q=${encodeURIComponent(q)}`);
  };

  return (
    <main>
      {/* HERO */}
      <section className="relative h-[560px] md:h-[620px]" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Hiker by a waterfall" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30" />
        </div>
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-white font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tight max-w-5xl animate-fadeup">
            Find your next waterfall
          </h1>
          <form
            onSubmit={onSearch}
            className="mt-10 w-full max-w-2xl bg-white rounded-full flex items-center p-2 animate-fadeup"
            style={{ animationDelay: "0.1s" }}
            data-testid="hero-search-form"
          >
            <Search className="w-5 h-5 text-stone-400 ml-4" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by city, country, or waterfall name"
              data-testid="hero-search-input"
              className="flex-1 py-3 px-3 bg-transparent outline-none text-stone-900 placeholder:text-stone-400"
            />
            <button
              type="submit"
              data-testid="hero-search-button"
              className="btn-at btn-at-dark"
            >
              Search
            </button>
          </form>
          <div className="mt-8 flex flex-col items-center gap-3 animate-fadeup" style={{ animationDelay: "0.2s" }}>
            <Link to="/waterfalls" className="text-white underline underline-offset-4 font-semibold" data-testid="hero-explore-link">
              Explore nearby waterfalls
            </Link>
            <Link to="/best-waterfalls" className="text-white underline underline-offset-4 font-semibold" data-testid="hero-bestof-link">
              Browse best-of lists
            </Link>
          </div>
        </div>
      </section>

      {/* Cold-start / network error banner with retry */}
      {loadErr && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 mt-12" data-testid="home-load-error">
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-extrabold text-amber-900">We couldn't load the waterfalls just now</h3>
              <p className="text-sm text-amber-800 mt-1">
                Our server may be waking up from idle, or your connection just hiccuped. Give it a moment and try again.
              </p>
              <button
                onClick={loadHome}
                data-testid="home-retry-btn"
                className="btn-at btn-at-dark text-sm mt-4"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Featured / Local favorites */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-20" data-testid="featured-section">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">
          Local favorites near <span className="heading-underline">you</span>
        </h2>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10">
          {loading && featured.length === 0
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} data-testid={`home-skeleton-${i}`} className="space-y-3 animate-pulse">
                  <div className="aspect-[4/5] bg-stone-200 rounded-2xl" />
                  <div className="h-4 bg-stone-200 rounded w-3/4" />
                  <div className="h-3 bg-stone-200 rounded w-1/2" />
                </div>
              ))
            : featured.slice(0, 8).map((w) => <WaterfallCard key={w.id} wf={w} />)}
        </div>
      </section>

      {/* More to explore (countries chips) */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-20">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">
          Explore by <span className="heading-underline">country</span>
        </h2>
        <div className="mt-6 chip-row no-scrollbar" data-testid="country-chip-row">
          {categories.countries.map((c) => (
            <Link
              key={c.slug}
              to={`/best-waterfalls-in/country/${c.slug}`}
              data-testid={`category-country-${c.slug}`}
              className="flex-shrink-0 rounded-full border border-stone-300 hover:border-stone-900 px-5 py-2 text-sm font-semibold text-stone-900"
            >
              {c.name} <span className="text-stone-500 font-normal">({c.count})</span>
            </Link>
          ))}
        </div>
      </section>

      {/* State / region grid */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">
          Best waterfalls by <span className="heading-underline">state &amp; region</span>
        </h2>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.states.slice(0, 16).map((s) => (
            <Link
              key={`${s.country_slug}-${s.slug}`}
              to={`/best-waterfalls-in/state/${s.slug}`}
              data-testid={`category-state-${s.slug}`}
              className="text-stone-900 hover:underline font-semibold"
            >
              Best waterfalls in {s.name}
              <span className="text-stone-500 font-normal"> ({s.count})</span>
            </Link>
          ))}
        </div>
      </section>

      {/* City grid */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16">
        <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-stone-900">
          Waterfalls by <span className="heading-underline">city</span>
        </h2>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.cities.slice(0, 24).map((c, i) => (
            <Link
              key={`${c.country}-${c.slug}-${i}`}
              to={`/best-waterfalls-in/city/${c.slug}`}
              data-testid={`category-city-${c.slug}`}
              className="text-stone-900 hover:underline font-semibold"
            >
              Waterfalls in {c.name}
              <span className="text-stone-500 font-normal"> ({c.count})</span>
            </Link>
          ))}
        </div>
      </section>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 mt-16">
        <SafetyBanner />
      </div>
    </main>
  );
}
